import { pushCtaEvent } from '@utils/dataLayer';
import { getAnalyticsContextFromElement } from '@utils/analyticsContext';

const pointerMQ = window.matchMedia('(pointer: coarse)');

export class HeroLanding {
  static validStates = [
    'preload',
    'transition',
    'prepare',
    'ready',
  ];

  static dragging = false;

  static allInstances = [];

  /**
   * Sets the interactive state for all HeroLanding instances.
   */
  static setAllInteractive() {
    HeroLanding.allInstances.forEach((instance) => {
      instance.setInteractive();
    });
  }

  constructor(element) {
    if (!element) {
      return;
    }

    this.parent = element;
    this.grid = this.parent.querySelector('.hero-landing__grid');
    this.slices = this.grid.querySelectorAll('.hero-landing__slice');
    this.buttons = this.grid.querySelectorAll('.hero-landing__link');

    if (this.parent.dataset.state === 'ready') {
      this.initInteractiveListeners();
    }

    HeroLanding.allInstances.push(this);

    const learnMoreButtons = this.parent.querySelectorAll('.hero-landing__button');
    learnMoreButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const wrapper = button.closest('.hero-landing__wrapper');
        const sliceTitle = wrapper?.querySelector('.hero-landing__title')?.textContent?.trim() || '';

        pushCtaEvent({
          section: 'Microsite Landing',
          context: sliceTitle || getAnalyticsContextFromElement(button),
          label: (button.textContent || '').trim() || 'Learn More',
        });
      });
    });
  }

  /**
   * Deselects all slices, returns to neutral state.
   */
  resetSlices = () => {
    this.parent.removeAttribute('data-slice-index');
  };

  /**
   * Sets the currently focussed slice for the given index.
   * @param {Number} index - The index of the slice to activate
   */
  focusSliceAtIndex(index) {
    if (this.parent.dataset.state !== 'ready') {
      return;
    }

    if (index < 0 || index > this.slices.length - 1) {
      this.resetSlices();
      return;
    }

    this.parent.dataset.sliceIndex = index;
  }

  /**
   * Resets dragging flag
   */
  /* eslint-disable-next-line class-methods-use-this */
  touchStartListener() {
    HeroLanding.dragging = false;
  }

  /**
   * Sets dragging flag
   */
  /* eslint-disable-next-line class-methods-use-this */
  touchMoveListener() {
    HeroLanding.dragging = true;
  }

  /**
   * Focusses or resets the slice state
   * @param {TouchEvent} event
   */
  touchEndListener = (event) => {
    const index = [...this.slices].indexOf(event.currentTarget);

    if (HeroLanding.dragging) {
      return;
    }

    if (event.target.closest('[href]')) {
      return;
    }

    if (this.parent.dataset.sliceIndex === `${index}`) {
      this.resetSlices();
    } else {
      this.focusSliceAtIndex(index);
    }
  };

  /**
   * Sets the slice state
   * @param {MouseEvent} event
   */
  mouseEnterListener = (event) => {
    const index = [...this.slices].indexOf(event.currentTarget);
    this.focusSliceAtIndex(index);
  };

  /**
   * Adds all event listeners for touch devices
   */
  setInteractiveListenersForTouch() {
    this.grid.addEventListener('touchstart', this.touchStartListener);

    this.grid.addEventListener('touchmove', this.touchMoveListener);

    this.slices.forEach((slice) => {
      slice.addEventListener('touchend', this.touchEndListener);
    });
  }

  /**
   * Removes all event listeners for touch devices
   */
  removeInteractiveListenersForTouch() {
    HeroLanding.dragging = false;

    this.grid.removeEventListener('touchstart', this.touchStartListener);

    this.grid.removeEventListener('touchmove', this.touchMoveListener);

    this.slices.forEach((slice) => {
      slice.removeEventListener('touchend', this.touchEndListener);
    });
  }

  /**
   * Adds all event listeners for mouse devices
   */
  setInteractiveListenersForMouse() {
    this.slices.forEach((slice) => {
      slice.addEventListener('mouseenter', this.mouseEnterListener);
    });

    this.grid.addEventListener('mouseleave', this.resetSlices);
  }

  /**
   * Removes all event listeners for mouse devices
   */
  removeInteractiveListenersForMouse() {
    this.slices.forEach((slice) => {
      slice.removeEventListener('mouseenter', this.mouseEnterListener);
    });

    this.grid.removeEventListener('mouseleave', this.resetSlices);
  }

  /**
   * Toggles between touch and mouse events
   */
  setInteractiveListenersForTouchPointerMq = () => {
    this.resetSlices();

    if (pointerMQ.matches) {
      this.removeInteractiveListenersForMouse();
      this.setInteractiveListenersForTouch();
    } else {
      this.removeInteractiveListenersForTouch();
      this.setInteractiveListenersForMouse();
    }
  };

  /**
   * Sets additional observers for interactive state.
   */
  initInteractiveListeners() {
    pointerMQ.addEventListener('change', this.setInteractiveListenersForTouchPointerMq);

    this.setInteractiveListenersForTouchPointerMq();

    this.slices.forEach((slice, index) => {
      slice.addEventListener('focusin', () => {
        this.focusSliceAtIndex(index);
      });

      slice.addEventListener('focusout', (event) => {
        const prevElement = event.target;
        const currentElement = event.relatedTarget;
        if (prevElement?.closest('.hero-landing__slice') && !currentElement?.closest('.hero-landing__slice')) {
          this.resetSlices();
        }
      });
    });

    const buttonResizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const width = `${entry.borderBoxSize[0].inlineSize}px`;
        const parentSlice = entry.target.closest('.hero-landing__slice');
        if (parentSlice.style.getPropertyValue('--hero-max-button-width') !== width) {
          parentSlice.style.setProperty('--hero-max-button-width', width);
        }
      });
    });

    [...this.buttons].forEach((button) => {
      buttonResizeObserver.observe(button, { box: 'border-box' });
    });
  }

  /**
   * Sets the initial page-load state.
   *
   * States:
   *      `preload` - Initial state, interactions blocked
   *   `transition` - Animation state, interactions blocked
   *      `prepare` - Intermediary state that resets CSS styles for hover animations
   *        `ready` - Final state, animations active
   *
   * @param {String} state - The loading state to swap to.
   */
  setState(state) {
    this.parent.dataset.state = HeroLanding.validStates.includes(state) ? state : 'ready';
  }

  /**
   * Triggers the preload animation.
   */
  setInteractive() {
    const boundListener = (event) => {
      if (event.target === this.grid) {
        this.grid.removeEventListener('transitionend', boundListener);

        // This fixes a bug that caused the slice to "pop" open instead of animate
        // if the user hovered over a slice at the same time at the intro
        // animation ended. The grid uses two different sets of units for the
        // intro and hover states that can't animate between themselves, so this
        // resets from one set of units to the other, and the ready adds the
        // transition attributes needed for the hover animations.
        setTimeout(() => {
          this.setState('prepare');
          setTimeout(() => {
            this.setState('ready');
            this.initInteractiveListeners();
          }, 150);
        }, 0);
      }
    };

    this.grid.addEventListener('transitionend', boundListener);
    this.setState('transition');
  }
}

function createHeroLandingInstances() {
  const elements = document.querySelectorAll('.hero-landing');
  elements.forEach((element) => {
    if (!element.heroLandingController) {
      /* eslint-disable no-param-reassign */
      element.heroLandingController = new HeroLanding(element);
    }
  });
}

export function loadHeroLanding() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createHeroLandingInstances);
  } else {
    createHeroLandingInstances();
  }
}

if (window.acf) {
  window.acf.addAction('render_block_preview/type=azds/hero-landing', loadHeroLanding);
}
